import { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNotifications } from '@/features/notifications/hooks/useNotifications';

/**
 * Notification Coverage Sweep — Phase 1.
 *
 * Cross-tenant lifecycle banner. Listens for any `TENANT_SUSPENDED_USER` /
 * `TENANT_MAINTENANCE` / `TENANT_ACTIVATED` notification in the user's
 * feed and renders a full-width banner above the main content. We don't
 * fetch tenant status directly — the backend has already broadcast the
 * notification so we use it as the source of truth.
 *
 * The banner is dismissable-by-status: when an ACTIVATED notification
 * shows up after a SUSPENDED one, the suspended state clears.
 */
export function TenantSuspendedBanner() {
  const { notifications } = useNotifications();

  // Find the most recent TENANT_* lifecycle notification.
  const lifecycle = useMemo(() => {
    const types = [
      'TENANT_SUSPENDED_USER',
      'TENANT_SUSPENDED',
      'TENANT_MAINTENANCE',
      'TENANT_ACTIVATED',
    ];
    return notifications.find((n) => types.includes(n.type)) ?? null;
  }, [notifications]);

  if (!lifecycle) return null;
  // Suppress the banner once the tenant is active again.
  if (lifecycle.type === 'TENANT_ACTIVATED') return null;

  const isMaintenance = lifecycle.type === 'TENANT_MAINTENANCE';
  const tone = isMaintenance
    ? 'bg-amber-50 border-amber-300 text-amber-900'
    : 'bg-rose-50 border-rose-300 text-rose-900';
  const heading = isMaintenance
    ? 'Tenant in maintenance mode'
    : 'Tenant suspended';
  const message = isMaintenance
    ? 'Your tenant is currently in maintenance mode. Some actions are temporarily disabled.'
    : 'Your tenant has been suspended by a platform administrator. New actions are disabled until the tenant is reactivated.';

  return (
    <div
      role="alert"
      className={`flex items-start gap-3 border-b px-4 py-2 text-sm ${tone}`}
    >
      <AlertTriangle className="size-4 mt-0.5 shrink-0" aria-hidden="true" />
      <div className="flex-1">
        <span className="font-medium">{heading}:</span> {message}
      </div>
    </div>
  );
}
